import { createApp, h } from 'vue';
import App from './App.vue';

import { router } from './router';
import '@/scss/style.scss';
import 'vue3-carousel/dist/carousel.css';
import 'vuefinder/dist/style.css';
import '@jaxtheprime/vue3-dropzone/dist/style.css';
// import 'vue3-easy-data-table/dist/style.css';
import 'vue3-toastify/dist/index.css';
import { registerPlugins } from '@/plugins';
import { Components } from './components';
import { GlobalVariables } from './globalviariables';
import { Initialize } from './initialize';
import { resizableColumns } from '@/directives/resizableColumns.js';
import { can } from '@/directives/can';
import 'pdfjs-dist/web/pdf_viewer.css'; // import pdfjs css
// import VueFinder from 'vuefinder/dist/vuefinder.js';
import VueExcelEditor from 'vue3-excel-editor';
import VueGtag, { trackRouter } from 'vue-gtag-next';
import { initMonitoring, identifySentryUser, ohsPublicPageView } from './plugins/monitoring';
import { toast } from 'vue3-toastify';
import escCloseDialogs from '@/plugins/escCloseDialogs';

// Which build is this? Answerable from a console on any environment, without
// repo access, by reading window.__APP_VERSION__. The same values are in
// /version.json for anyone asking from outside the browser.
window.__APP_VERSION__ = { version: __BUILD_VERSION__, commit: __BUILD_COMMIT__ };

// Listen for session expiry and warn the user before redirect
window.addEventListener('session-expired', ((event: CustomEvent) => {
  try {
    toast.error('Your session has expired. You will be redirected to login.', {
      autoClose: false,
      position: 'top-center',
    });
  } catch (error) {
    console.error('Failed to display session expiry toast:', error);

    // Track critical toast failure in Sentry
    if (window.Sentry) {
      window.Sentry.captureException(error, {
        tags: {
          error_type: 'critical_toast_failed',
          toast_type: 'session_expired',
          component: 'main.ts'
        },
        contexts: {
          session_expiry: {
            url: event.detail?.url || window.location.href,
            message: 'Session expiry warning toast failed to display'
          }
        }
      });
    }

    // Fallback: Use native alert if toast fails
    alert('Your session has expired. You will be redirected to login.');
  }
}) as EventListener);

/**
 * Report the outcome of the Xero consent round-trip.
 *
 * The backend redirects here with ?xero=connected|error|org_mismatch. The
 * callback now lands on Company Settings → Accounting, which shows the outcome
 * as a banner next to the connect button (XeroSettings) — so this global toast
 * only covers landings ELSEWHERE, e.g. an older XERO_RETURN_URL still pointing
 * at the app root. The param is stripped afterwards so a refresh doesn't re-toast.
 */
(() => {
  try {
    // Leave the param alone on the settings page — XeroSettings owns it there.
    if (window.location.pathname.startsWith('/company-settings')) return;

    const params = new URLSearchParams(window.location.search);
    const status = params.get('xero');
    if (!status) return;

    if (status === 'connected') {
      toast.success('Connected to Xero.');
    } else if (status === 'org_mismatch') {
      // A DIFFERENT Xero organisation was authorised. The backend refused rather
      // than repointing the connection, which would have orphaned every job
      // mapping and pushed-invoice link. Must be explicit about the fix.
      toast.error(
        'That was a different Xero organisation. Your existing job links and invoices belong to the ' +
        'organisation already connected, so nothing was changed. Reconnect the same organisation, or ' +
        'use "Remove all Xero data" in Accounting settings first if you really are switching.',
        { autoClose: false, position: 'top-center' }
      );
    } else {
      toast.error("Couldn't connect to Xero. Please try again.");
    }

    params.delete('xero');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
  } catch (error) {
    console.error('Failed to report Xero connection status:', error);
  }
})();

const app = createApp(App);

// Global column-resize directive (v-resizable-columns) — available on every table.
app.directive('resizable-columns', resizableColumns);

// Global permission gate (v-can) — available on every control.
//
// The matrix's Add/Edit/Delete columns had no client-side gate at all, so a
// View-only role saw every action button and only found out on the request.
// See src/directives/can.ts.
app.directive('can', can);

// Suppress Vue warnings in console (production and development)
app.config.warnHandler = () => {};

// Disable performance warnings and devtools in production
if (import.meta.env.PROD) {
  app.config.performance = false;
}

registerPlugins(app);
Components(app);
app.use(router);
app.use(VueExcelEditor);
// Esc closes the topmost dialog app-wide. Vuetify only does this for
// non-`persistent` dialogs, and most of ours are persistent.
app.use(escCloseDialogs);

// Google Analytics 4 - must be before GlobalVariables so $gtag is available.
//
// The id comes from the environment and is set ONLY in the production build.
// It used to be hardcoded, so staging, dev and localhost all reported into the
// same GA4 stream as production: about 29% of the events Google Ads imports as
// conversions were developers registering test accounts, which is exactly the
// signal Smart Bidding would have trained on once campaigns unpause.
//
// The plugin is still installed when the id is absent, with isEnabled false.
// That keeps `$gtag` defined for the 17 `this.$gtag.event(...)` call sites
// around the app (removing the plugin would make every one of them throw)
// while loading no script and sending no hits.
const ga4MeasurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID || '';
app.use(VueGtag, {
  property: { id: ga4MeasurementId },
  appName: 'BuiltSimple',
  isEnabled: Boolean(ga4MeasurementId),
  useDebugger: false
});

// Track page views with router (an OHS public page's credential is kept out of its page view)
trackRouter(router, { template: ohsPublicPageView });

// Initialize Sentry + Microsoft Clarity monitoring
initMonitoring(app, router);

// A production build with no measurement id boots normally and sends nothing.
// That failure looks exactly like "nobody signed up today", which is the shape
// nobody reports, so say it out loud on the first page load.
//
// Checked on MODE, not PROD: `vite build --mode staging` sets PROD true as well,
// and a blank id is the intended configuration on staging and development.
// Raised after initMonitoring so Sentry is listening and the error is reported,
// rather than sitting in a console nobody has open.
if (import.meta.env.MODE === 'production' && !ga4MeasurementId) {
  console.error('[analytics] VITE_GA4_MEASUREMENT_ID is not set: no GA4 events or Ads conversions will be sent');
}

// Identify existing logged-in user in Sentry + PostHog on app boot
// (so returning visitors are immediately recognised, not anonymous)
try {
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    const userData = JSON.parse(storedUser);
    if (userData?.id) {
      identifySentryUser(
        String(userData.id),
        userData.email || '',
        userData.name || userData.username || '',
        {
          company_name: userData.company_name || userData.company,
          company_id: userData.company_id,
          user_role: userData.role || userData.user_role,
          subscription_tier: userData.subscription_tier || userData.tier || userData.plan,
          subscription_status: userData.subscription_status,
          signup_date: userData.created_at || userData.signup_date,
          is_admin: userData.is_admin,
          is_owner: userData.is_owner,
          team_size: userData.team_size,
          trial_ends_at: userData.trial_ends_at,
          country: userData.country,
        }
      );

      // Prime the upload-config store so Vue3Dropzone instances reflect the
      // server-side limit (config/upload.php → nginx) instead of the
      // package's 5MB default.
      import('./stores/uploadConfig').then(({ useUploadConfigStore }) => {
        useUploadConfigStore().ensureLoaded();
      });
    }
  }
} catch (e) {
  // Ignore parse errors
}

// GlobalVariables after VueGtag so useGtag() works
GlobalVariables(app, router);

app.mount('#app');
// app.use(router).mount('#app');
Initialize();

// Initialize Socket.IO for real-time notifications
const initializeSocketIO = async () => {
  try {
    // Check if user is logged in
    const user = localStorage.getItem('user');
    if (!user) {
      console.log('⏳ User not logged in, skipping Socket.IO initialization');
      return;
    }

    const userData = JSON.parse(user);
    if (!userData || !userData.token) {
      console.log('⏳ No auth token found, skipping Socket.IO initialization');
      return;
    }

    console.log('🔌 Initializing Socket.IO...');

    // Import socket service and connect
    const { socketService } = await import('./services/socketService');
    await socketService.connect(userData.token);

    console.log('✅ Socket.IO initialized successfully');

    // Initialize notification store
    const { useNotificationStore } = await import('./stores/notifications');
    const notificationStore = useNotificationStore();
    await notificationStore.initialize();

    console.log('✅ Notification store initialized');

    // Make socket service available globally for debugging
    (window as any).socketService = socketService;
    console.log('💡 Run window.socketService in console to check socket status');

  } catch (error) {
    // Use console.log (not error) — Sentry's captureConsoleIntegration only listens
    // to error/warn levels. Socket failures are non-critical (app works without realtime)
    // and would otherwise pollute Sentry with transient transport errors.
    console.log('⚠️ Socket.IO init skipped:', (error as Error)?.message || error);
  }
};

// Start initialization when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSocketIO);
} else {
  initializeSocketIO();
}
